import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Card, Typography, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Snackbar,
  Alert, CircularProgress, Skeleton, AlertColor, IconButton, Tooltip,
} from "@mui/material";
import { Refresh, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import * as lordsApi from "../../services/lordsApi";

/* ─── TYPES ────────────────────────────────────────────── */
interface Agent {
  id: number;
  username: string;
  role: string;
  account_type: string;
  credit: number;
  available_credit: number;
  exposure: number;
  gt: number;
}

interface RowState {
  amount: string;
  status: "idle" | "loading" | "success" | "error";
  msg: string;
}

interface SnackState { open: boolean; msg: string; severity: AlertColor; }

const fmt = (n: number | null | undefined) =>
  n == null ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   G/T TRANSFER (BALANCE SETTLEMENT) TAB
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const TransferTab: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [txnPwd, setTxnPwd] = useState("");
  const [snack, setSnack] = useState<SnackState>({ open: false, msg: "", severity: "info" });
  const [batchLoading, setBatchLoading] = useState(false);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await lordsApi.getAllDetails({ page: 1, limit: 100 });
      setAgents(data.users || []);
      // Initialize row states
      const init: Record<string, RowState> = {};
      (data.users || []).forEach((a: Agent) => {
        init[`${a.account_type}-${a.id}`] = { amount: "", status: "idle", msg: "" };
      });
      setRowStates(init);
    } catch (e) {
      setSnack({ open: true, msg: "Failed to load agents", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const getKey = (a: Agent) => `${a.account_type}-${a.id}`;

  const setRowAmount = (a: Agent, val: string) => {
    setRowStates((s) => ({ ...s, [getKey(a)]: { ...s[getKey(a)], amount: val, status: "idle", msg: "" } }));
  };

  const fillAll = (a: Agent) => {
    // "All" button: set amount = -GT (full settlement)
    const amount = -Number(a.gt);
    setRowStates((s) => ({ ...s, [getKey(a)]: { ...s[getKey(a)], amount: String(amount) } }));
  };

  const handleTransfer = async (a: Agent) => {
    const key = getKey(a);
    const rs = rowStates[key];
    if (!rs?.amount || Number(rs.amount) === 0) return;
    if (!txnPwd) {
      setSnack({ open: true, msg: "Enter transaction password", severity: "error" });
      return;
    }

    setRowStates((s) => ({ ...s, [key]: { ...s[key], status: "loading", msg: "" } }));

    try {
      await lordsApi.gtTransfer({
        userId: a.id,
        amount: Number(rs.amount),
        recieverType: a.role,
        transactionPassword: txnPwd,
      });
      setRowStates((s) => ({ ...s, [key]: { amount: "", status: "success", msg: "Done" } }));
      // Refresh after 1.2s
      setTimeout(fetchAgents, 1200);
    } catch (e: any) {
      setRowStates((s) => ({
        ...s,
        [key]: { ...s[key], status: "error", msg: e?.message || "Failed" },
      }));
    }
  };

  const handleTransferAll = async () => {
    if (!txnPwd) {
      setSnack({ open: true, msg: "Enter transaction password", severity: "error" });
      return;
    }
    setBatchLoading(true);
    const toSettle = agents.filter((a) => Number(a.gt) !== 0);

    for (const a of toSettle) {
      const amount = -Number(a.gt);
      const key = getKey(a);
      setRowStates((s) => ({ ...s, [key]: { amount: String(amount), status: "loading", msg: "" } }));

      try {
        await lordsApi.gtTransfer({
          userId: a.id,
          amount,
          recieverType: a.role,
          transactionPassword: txnPwd,
        });
        setRowStates((s) => ({ ...s, [key]: { amount: "", status: "success", msg: "Done" } }));
      } catch (e: any) {
        setRowStates((s) => ({
          ...s,
          [key]: { ...s[key], status: "error", msg: e?.message || "Failed" },
        }));
      }
    }

    setBatchLoading(false);
    setTimeout(fetchAgents, 1200);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" sx={{ color: "#F9F9F9", fontWeight: 700 }}>
          G/T Settlement (Balance Transfer)
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" label="Transaction Password" type="password"
            value={txnPwd} onChange={(e) => setTxnPwd(e.target.value)}
            sx={{ width: 220, "& .MuiInputBase-root": { bgcolor: "#10182E" } }} />
          <Button variant="contained" onClick={handleTransferAll} disabled={batchLoading}
            sx={{ bgcolor: "#2B6EF5", "&:hover": { bgcolor: "#4A85F7" }, textTransform: "none" }}>
            {batchLoading ? <CircularProgress size={20} /> : "Transfer All"}
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAgents} sx={{ color: "#8384A5" }}><Refresh /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <Card sx={{ bgcolor: "#0E1831", border: "1px solid #1E2D55", borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#0A0F1F" }}>
                {["Username", "Role", "Credit", "Exposure", "Available Balance", "G/T", "Transfer Amount", "Action", "Status"].map((h) => (
                  <TableCell key={h} sx={{ color: "#8384A5", fontWeight: 600, fontSize: 12, borderBottom: "1px solid #1E2D55", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j} sx={{ borderBottom: "1px solid #1E2D55" }}>
                        <Skeleton variant="text" sx={{ bgcolor: "#1E2D55" }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: "#8384A5", py: 4, borderBottom: "1px solid #1E2D55" }}>
                    No agents found
                  </TableCell>
                </TableRow>
              ) : (
                agents.map((a) => {
                  const key = getKey(a);
                  const rs = rowStates[key] || { amount: "", status: "idle", msg: "" };
                  const gt = Number(a.gt);
                  return (
                    <TableRow key={key} sx={{ "&:hover": { bgcolor: "#162140" } }}>
                      <TableCell sx={{ color: "#F9F9F9", borderBottom: "1px solid #1E2D55", fontSize: 13, fontWeight: 600 }}>
                        {a.username}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #1E2D55" }}>
                        <Chip label={a.role} size="small" sx={{ fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ color: "#F9F9F9", borderBottom: "1px solid #1E2D55", fontSize: 13 }}>
                        {fmt(a.credit)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #1E2D55", fontSize: 13 }}>
                        <Typography sx={{ color: (a.exposure || 0) > 0 ? "#E01B4F" : "#0ECC68", fontSize: 13 }}>
                          {fmt(a.exposure || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: "#F9F9F9", borderBottom: "1px solid #1E2D55", fontSize: 13 }}>
                        {fmt(a.available_credit)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #1E2D55" }}>
                        <Typography sx={{ color: gt >= 0 ? "#0ECC68" : "#E01B4F", fontWeight: 700, fontSize: 13 }}>
                          {fmt(gt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #1E2D55" }}>
                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                          <TextField size="small" type="number" value={rs.amount}
                            onChange={(e) => setRowAmount(a, e.target.value)}
                            disabled={gt === 0}
                            placeholder={gt === 0 ? "Settled" : "Amount"}
                            sx={{ width: 120, "& .MuiInputBase-root": { bgcolor: "#10182E", height: 32, fontSize: 13 } }} />
                          {gt !== 0 && (
                            <Button size="small" onClick={() => fillAll(a)}
                              sx={{ color: "#2B6EF5", minWidth: 40, fontSize: 11, textTransform: "none" }}>
                              All
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #1E2D55" }}>
                        <Button size="small" variant="contained" onClick={() => handleTransfer(a)}
                          disabled={gt === 0 || rs.status === "loading"}
                          sx={{ bgcolor: "#2B6EF5", "&:hover": { bgcolor: "#4A85F7" }, fontSize: 11, textTransform: "none", minWidth: 60 }}>
                          {rs.status === "loading" ? <CircularProgress size={16} /> : "Submit"}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #1E2D55" }}>
                        {rs.status === "success" && (
                          <Chip size="small" icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Done"
                            sx={{ bgcolor: "rgba(14,204,104,0.12)", color: "#0ECC68", fontSize: 11 }} />
                        )}
                        {rs.status === "error" && (
                          <Chip size="small" icon={<ErrorIcon sx={{ fontSize: 14 }} />} label={rs.msg}
                            sx={{ bgcolor: "rgba(224,27,79,0.12)", color: "#E01B4F", fontSize: 11 }} />
                        )}
                        {gt === 0 && rs.status === "idle" && (
                          <Typography sx={{ color: "#8384A5", fontSize: 11 }}>Nothing to clear</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransferTab;
